import { confirmDialog } from "@/components/ui";
import { User, VideoProject } from "@/services/bedrock";

export function resolveKanbanActorUserId(userId: number | string | null | undefined): number | undefined {
  if (userId == null || userId === "") return undefined;
  const parsed = Number(userId);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function staffDisplayName(user: User): string {
  const fullName = user.fullName?.trim();
  if (fullName) return fullName;

  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  if (name.length > 0) return name;

  return user.email || `User ${user.id}`;
}

function placeholderStaffUser(id: number): User {
  return {
    id,
    email: null,
    firstName: null,
    lastName: null,
    fullName: null,
    phone: null,
    photoUrl: null,
  };
}

export function resolveStaffUser(
  userId: number | null | undefined,
  staffUsers: User[],
  assignments: VideoProject.QueueAssignment[] = []
): User | undefined {
  if (userId == null) return undefined;

  return (
    staffUsers.find(user => user.id === userId) ??
    assignments.find(assignment => assignment.staffUserId === userId)?.staffUser ??
    placeholderStaffUser(userId)
  );
}

export function recommendedStaffForRole(
  project: VideoProject,
  role: VideoProject.QueueAssignment["role"],
  staffUsers: User[]
): User[] {
  const assignments = project.queueAssignments?.filter(assignment => assignment.role === role) ?? [];
  const users: User[] = [];
  const seen = new Set<number>();

  for (const assignment of assignments) {
    if (seen.has(assignment.staffUserId)) continue;
    seen.add(assignment.staffUserId);
    users.push(
      assignment.staffUser ??
        staffUsers.find(user => user.id === assignment.staffUserId) ??
        placeholderStaffUser(assignment.staffUserId)
    );
  }

  return users.sort((a, b) => staffDisplayName(a).localeCompare(staffDisplayName(b)));
}

export function sortStaffWithCurrentUserFirst(users: User[], currentUserId?: number): User[] {
  if (currentUserId == null) return users;
  const currentUser = users.find(user => user.id === currentUserId);
  if (!currentUser) return users;
  return [currentUser, ...users.filter(user => user.id !== currentUserId)];
}

export function assignableEditorUsers(users: User[]): User[] {
  const hasGroupData = users.some(user => user.userGroups != null);
  if (!hasGroupData) return users;
  return users.filter(user => user.status === "ACTIVE" && user.userGroups?.includes(User.Type.EDITOR));
}

const EDITOR_UNASSIGNABLE_STATUSES = new Set<VideoProject.Status>([VideoProject.Status.SUBMITTED_FOR_EDITING]);

const QA_UNASSIGNABLE_STATUSES = new Set<VideoProject.Status>([
  VideoProject.Status.SUBMITTED_FOR_EDITING,
  VideoProject.Status.EDITING,
  VideoProject.Status.SUBMITTED_FOR_TESTING,
]);

export function canUnassignKanbanStaff(
  role: VideoProject.QueueAssignment["role"],
  status: VideoProject.Status
): boolean {
  if (role === "EDITOR") return EDITOR_UNASSIGNABLE_STATUSES.has(status);
  return QA_UNASSIGNABLE_STATUSES.has(status);
}

export function staffRoleLabel(role: VideoProject.QueueAssignment["role"]): "Editor" | "QA" {
  return role === "EDITOR" ? "Editor" : "QA";
}

export function otherStaffRole(role: VideoProject.QueueAssignment["role"]): VideoProject.QueueAssignment["role"] {
  return role === "EDITOR" ? "QA" : "EDITOR";
}

export function isSamePersonEditorQaAssignment(
  userId: number | null | undefined,
  otherRoleUserId: number | null | undefined
): boolean {
  return userId != null && otherRoleUserId != null && userId === otherRoleUserId;
}

export function isNewSamePersonEditorQaAssignment(
  userId: number | null | undefined,
  otherRoleUserId: number | null | undefined,
  currentRoleUserId?: number | null
): boolean {
  if (!isSamePersonEditorQaAssignment(userId, otherRoleUserId)) return false;
  return currentRoleUserId !== userId;
}

export function assignedUserIdForRole(
  project: Pick<VideoProject, "editorUserId" | "qaUserId">,
  role: VideoProject.QueueAssignment["role"]
): number | null | undefined {
  return role === "EDITOR" ? project.editorUserId : project.qaUserId;
}

export async function confirmSamePersonEditorQaAssignment(options: {
  assigningRole: VideoProject.QueueAssignment["role"];
  userName?: string;
}): Promise<boolean> {
  const thisRole = staffRoleLabel(options.assigningRole);
  const otherRole = staffRoleLabel(otherStaffRole(options.assigningRole));
  const name = options.userName?.trim() || "This user";

  return confirmDialog({
    title: "Same person as Editor and QA",
    description: `${name} is already assigned as ${otherRole}. Assign them as ${thisRole} anyway?`,
    confirmLabel: "Assign anyway",
    cancelLabel: "Cancel",
  });
}

export function assigningRoleForKanbanEvent(event: VideoProject.Event): VideoProject.QueueAssignment["role"] | null {
  if (event === VideoProject.Event.START_EDITING) return "EDITOR";
  if (event === VideoProject.Event.START_TESTING) return "QA";
  return null;
}

export function kanbanEventAssignsSamePerson(
  project: Pick<VideoProject, "editorUserId" | "qaUserId">,
  event: VideoProject.Event,
  userId: number | undefined
): boolean {
  const assigningRole = assigningRoleForKanbanEvent(event);
  if (!assigningRole || userId == null) return false;
  const otherRoleUserId = assigningRole === "EDITOR" ? project.qaUserId : project.editorUserId;
  const currentRoleUserId = assignedUserIdForRole(project, assigningRole);
  return isNewSamePersonEditorQaAssignment(userId, otherRoleUserId, currentRoleUserId);
}

export async function confirmKanbanEventSamePersonAssignment(
  project: Pick<VideoProject, "editorUserId" | "qaUserId">,
  event: VideoProject.Event,
  userId: number | undefined,
  staffUsers: User[]
): Promise<boolean> {
  if (!kanbanEventAssignsSamePerson(project, event, userId) || userId == null) return true;

  const assigningRole = assigningRoleForKanbanEvent(event);
  if (!assigningRole) return true;

  const user = staffUsers.find(staff => staff.id === userId);
  return confirmSamePersonEditorQaAssignment({
    assigningRole,
    userName: user ? staffDisplayName(user) : undefined,
  });
}

export function stopKanbanPointer(event: { stopPropagation(): void }) {
  event.stopPropagation();
}
